import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { t } from 'i18next';
import { snakeCase, startCase } from 'lodash-es';

import { getJobStateLabel } from '~components/Job/JobStatusChip';
import { getOrderStateLabel } from '~components/Order/OrderStatusChip';
import { Job } from '~hooks/useJob/models';
import { getFormattedRateValue } from '~pages/Settings/RatesManagement/rateUtils';
import { ExportColumn } from '~utils/export/exportColumn';
import {
  extractRateInfoFromJob,
  getLargestDeliveredQuantitySum,
} from '~utils/jobs/job-utils';
import { extractWaypointData } from '~utils/orders';
import { numberToIntegerOrDecimal } from '~utils/utilFunctions';

dayjs.extend(duration);

const DISPATCH_EXPORT_PDF_WIDTH = {
  medium: 13,
  narrow: 9,
  wide: 20,
};

export function getJobStartTimeText(job: Job) {
  return job.jobStartAt ? dayjs.tz(job.jobStartAt).format('hh:mm A') : '';
}

export function getJobStartDateText(job: Job) {
  return job.jobStartAt ? dayjs.tz(job.jobStartAt).format('DD-MMM-YYYY') : '';
}

export function getJobCycleTimeText(job: Pick<Job, 'loadCycleAvg'>) {
  if (!job.loadCycleAvg) return '';
  const hours = Math.floor(
    dayjs.duration({ minutes: job.loadCycleAvg }).asHours(),
  ).toString();
  const minutes = dayjs
    .duration({ minutes: job.loadCycleAvg % 60 })
    .asMinutes()
    .toString();
  return `${hours}hr ${minutes}m`;
}

export function getJobMaterialText(job: Job) {
  return job.material?.name ?? '';
}

export function getJobPickupSiteText(job: Job) {
  return extractWaypointData(job.pickUpWayPoints?.[0])?.name || '-';
}

export function getJobDropoffSiteText(job: Job) {
  return extractWaypointData(job.dropOffWayPoints?.[0])?.name || '-';
}

export function getJobPickupSiteAddressText(job: Job) {
  return extractWaypointData(job.pickUpWayPoints?.[0])?.fullAddress || '';
}

export function getJobDropoffSiteAddressText(job: Job) {
  return extractWaypointData(job.dropOffWayPoints?.[0])?.fullAddress || '';
}

export function getJobTruckIdText(job: Job) {
  if (job.equipment?.name) return job.equipment.name;
  return job.equipment?.externalId || '';
}

export function getJobTrailerText(job: Job) {
  if (job.additionalEquipment?.name) return job.additionalEquipment.name;
  return job.additionalEquipment?.externalId || '';
}

export function getJobRequestedQuantityText(job: Job) {
  return job.quantity ? numberToIntegerOrDecimal(Number(job.quantity)) : '';
}

export function getJobRequestedUnitText(job: Job) {
  return job.unitOfMeasure?.name ? startCase(job.unitOfMeasure.name) : '';
}

export function getJobDeliveredQuantityText(job: Job) {
  const ticketsCount = job.jobSummary?.loadsWithTicketsCount ?? 0;
  const { total } = getLargestDeliveredQuantitySum(
    job.jobSummary?.deliveredQuantities ?? [],
  );
  const shouldShowSum = ticketsCount > 0 && total > 0;
  return shouldShowSum ? total.toFixed(2) : '';
}

export function getJobDeliveredUnitText(job: Job) {
  const ticketsCount = job.jobSummary?.loadsWithTicketsCount ?? 0;
  const { total, unitOfMeasure } = getLargestDeliveredQuantitySum(
    job.jobSummary?.deliveredQuantities ?? [],
  );
  const shouldShowSum = ticketsCount > 0 && total > 0;
  return shouldShowSum && unitOfMeasure ? startCase(unitOfMeasure) : '';
}

export function getJobCompletedLoadsText(job: Job) {
  const completedLoadsCount =
    job.jobSummary?.completedLoadsCount ?? job.completedLoadsCount;
  return completedLoadsCount === undefined ? '' : String(completedLoadsCount);
}

export function getJobEndTimeText(job: Job) {
  const loggingEndedAt = job.jobSummary?.loggingEndedAt;
  if (!loggingEndedAt) return '';
  return `${dayjs.tz(loggingEndedAt).format('hh:mm A')} ${dayjs.tz(loggingEndedAt).format('DD-MMM-YYYY')}`;
}

function getJobPayRateInfo(job: Job) {
  return extractRateInfoFromJob(job, 'pay') ?? extractRateInfoFromJob(job, 'driver');
}

export function getJobRateTypeText(job: Job) {
  const rateInfo = getJobPayRateInfo(job);
  return rateInfo ? t(`rate_types.${snakeCase(rateInfo.rateType)}`) : '';
}

export function makeDispatchExportColumns(
  currencyFormatter: (value: number | string) => string,
  dispatchNumberDisplayHeader?: string,
): ExportColumn<Job>[] {
  return [
    {
      id: 'startTime',
      header: t('export.columns.start_time'),
      defaultSelected: true,
      onScreen: true,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.medium,
      get: (job) =>
        [getJobStartTimeText(job), getJobStartDateText(job)].filter(Boolean).join(' '),
    },
    {
      id: 'dispatchNumber',
      header: t('export.columns.dispatch_number'),
      displayHeader: dispatchNumberDisplayHeader,
      defaultSelected: true,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.wide,
      get: (job) => job.order?.dispatchNumber?.name || '',
    },
    {
      id: 'vendor',
      header: t('export.columns.vendor'),
      defaultSelected: true,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.medium,
      get: (job) => job.vendorJobAssignment?.vendorAccount?.name || '',
    },
    {
      id: 'driver',
      header: t('export.columns.driver'),
      defaultSelected: true,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.medium,
      get: (job) => job.driverFullName || '',
    },
    {
      id: 'phone',
      header: t('export.columns.phone'),
      defaultSelected: true,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: (job) => job.driver?.phone || '',
    },
    {
      id: 'requestedEquipmentType',
      header: t('export.columns.requested_equipment_type_name'),
      defaultSelected: true,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.medium,
      get: (job) => job.requestedEquipmentTypeName || '',
    },
    {
      id: 'truckId',
      header: t('export.columns.truck_id'),
      defaultSelected: true,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: getJobTruckIdText,
    },
    {
      id: 'customer',
      header: t('export.columns.customer'),
      defaultSelected: true,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.wide,
      get: (job) => job.order?.account?.name || '',
    },
    {
      id: 'pickupSite',
      header: t('export.columns.pickup_site_name'),
      defaultSelected: true,
      onScreen: true,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.wide,
      get: getJobPickupSiteText,
    },
    {
      id: 'pickupSiteAddress',
      header: t('export.columns.pickup_site_address'),
      defaultSelected: false,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.wide,
      get: getJobPickupSiteAddressText,
    },
    {
      id: 'dropoffSite',
      header: t('export.columns.dropoff_site_name'),
      defaultSelected: true,
      onScreen: true,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.wide,
      get: getJobDropoffSiteText,
    },
    {
      id: 'dropoffSiteAddress',
      header: t('export.columns.dropoff_site_address'),
      defaultSelected: false,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.wide,
      get: getJobDropoffSiteAddressText,
    },
    {
      id: 'jobService',
      header: t('export.columns.job_service'),
      defaultSelected: true,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.medium,
      get: (job) => job.service?.name || '',
    },
    {
      id: 'jobStatus',
      header: t('export.columns.job_status'),
      defaultSelected: true,
      onScreen: true,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.medium,
      get: (job) => getJobStateLabel(job.status),
    },
    {
      id: 'requestedQuantity',
      header: t('export.columns.job_quantity'),
      defaultSelected: true,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: getJobRequestedQuantityText,
    },
    {
      id: 'requestedUnit',
      header: t('export.columns.job_quantity_unit'),
      defaultSelected: true,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: getJobRequestedUnitText,
    },
    {
      id: 'material',
      header: t('export.columns.material'),
      defaultSelected: true,
      onScreen: true,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.medium,
      get: getJobMaterialText,
    },
    {
      id: 'friendlyJobId',
      header: t('export.columns.friendly_job_id'),
      defaultSelected: true,
      onScreen: true,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: (job) => job.jobId || '',
    },
    {
      id: 'orderStatus',
      header: t('export.columns.order_status'),
      defaultSelected: true,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.medium,
      get: (job) => getOrderStateLabel(job.order.state),
    },
    {
      id: 'endTime',
      header: t('export.columns.end_time'),
      defaultSelected: false,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.medium,
      get: getJobEndTimeText,
    },
    {
      id: 'deliveredQuantity',
      header: t('export.columns.delivered_quantity'),
      defaultSelected: false,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: getJobDeliveredQuantityText,
    },
    {
      id: 'deliveredUnit',
      header: t('export.columns.delivered_unit'),
      defaultSelected: false,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: getJobDeliveredUnitText,
    },
    {
      id: 'completedLoads',
      header: t('export.columns.completed_loads'),
      defaultSelected: false,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: getJobCompletedLoadsText,
    },
    {
      id: 'trailer',
      header: t('export.columns.trailer'),
      defaultSelected: false,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: getJobTrailerText,
    },
    {
      id: 'cycleTime',
      header: t('export.columns.cycle_time'),
      defaultSelected: false,
      onScreen: true,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: getJobCycleTimeText,
    },
    {
      id: 'jobRateValue',
      header: t('export.columns.job_rate_value'),
      defaultSelected: false,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: (job) => {
        const rateInfo = getJobPayRateInfo(job);
        if (!rateInfo) return '';
        return getFormattedRateValue(
          rateInfo.rateType,
          rateInfo.rateValue,
          currencyFormatter,
          true,
        );
      },
    },
    {
      id: 'jobRateType',
      header: t('export.columns.job_rate_type'),
      defaultSelected: false,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.narrow,
      get: getJobRateTypeText,
    },
    {
      id: 'orderName',
      header: t('export.columns.order_name'),
      defaultSelected: false,
      onScreen: false,
      pdfWidth: DISPATCH_EXPORT_PDF_WIDTH.wide,
      get: (job) => job.order?.name || job.projectName || '',
    },
  ];
}
